/*
 * Sketch to Slide — places the selected sketch photo in Azure Blob Storage so
 * sketchToSlideApi.ts's POST /image/sketch-to-slide has a blob reference to
 * read (that endpoint downloads the blob server-side; it does not accept
 * inline base64 — see sketchToSlideApi.ts's header comment).
 *
 * Upload mechanics (blob-sas request + PUT) live in shared/blobImageUpload.ts,
 * shared with photoHeadUpload.ts. Only this feature's allowed-format table
 * and file naming stay here — the sketch-to-slide route's fetch_sketch_image()
 * only recognises png/jpg/jpeg/webp/bmp (no gif) — see DeckorateAPI's
 * services/sketch_to_slide_api/image.py::_MIME_BY_EXT.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { BlobUploadReasons, BlobUploadResult, ImageFormatSignature } from "../../shared/services/blobImageUpload";
import { detectExtensionFromBytes, uploadImageBlob } from "../../shared/services/blobImageUpload";
import { logSketchToSlideError } from "./sketchToSlideApi";

export type SketchToSlideUploadResult = BlobUploadResult;

const BLOB_SAS_REQUEST_FAILED_MESSAGE =
  "Couldn't get a place to upload this sketch — the blob-sas request didn't return a usable asset.";
const BLOB_PUT_FAILED_MESSAGE = "Uploading the sketch to blob storage failed.";

const UPLOAD_MESSAGES = { blobSasFailed: BLOB_SAS_REQUEST_FAILED_MESSAGE, blobPutFailed: BLOB_PUT_FAILED_MESSAGE };

const UPLOAD_REASONS: BlobUploadReasons = {
  formatSniff: "sketch-to-slide-format-sniff",
  sasRequest: "sketch-to-slide-blob-sas-request",
  sasVersionParse: "sketch-to-slide-sas-version-parse",
  putEncode: "sketch-to-slide-blob-put-encode",
  putNetwork: "sketch-to-slide-blob-put-network",
  putBodyRead: "sketch-to-slide-blob-put-body-read",
};

/** Only formats DeckorateAPI's sketch-to-slide route actually accepts (no gif). */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/bmp": "bmp",
};

/** Magic-number signatures, checked in order — first bytes of the decoded image.
 *  WEBP needs two non-adjacent checks (the RIFF container header, then the WEBP
 *  fourCC at byte 8), so each signature carries a list of checks instead of one. */
const FORMAT_SIGNATURES: readonly ImageFormatSignature[] = [
  { extension: "png", checks: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] }] },
  { extension: "jpg", checks: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }] },
  { extension: "bmp", checks: [{ offset: 0, bytes: [0x42, 0x4d] }] },
  {
    extension: "webp",
    checks: [
      { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] }, // "RIFF"
      { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] }, // "WEBP"
    ],
  },
];

/** WEBP's check reads up to byte 11; base64 chars come in groups of 4 -> 3 bytes,
 * so 16 chars (4 groups) safely decodes to 12 bytes without a padding edge case. */
const SIGNATURE_PROBE_BASE64_LENGTH = 16;

function fileNameFor(base64: string, mimeType: string): string {
  const extension =
    detectExtensionFromBytes(base64, FORMAT_SIGNATURES, SIGNATURE_PROBE_BASE64_LENGTH, logSketchToSlideError, UPLOAD_REASONS.formatSniff) ??
    EXTENSION_BY_MIME_TYPE[mimeType] ??
    "png";
  return `sketch.${extension}`;
}

/**
 * Upload the given sketch photo to Azure Blob Storage and return a blob
 * reference for sketchToSlideApi.ts's POST /image/sketch-to-slide.
 *
 * @param http Deckorate HttpClient (used only for the blob-sas request).
 * @param clientId Studio client id (StudioClient.id) — the tenant the blob belongs to.
 * @param base64 Image bytes, base64-encoded, no `data:` prefix. The file_name sent to
 *   blob-sas is derived from these bytes' actual magic-number signature, not `mimeType`,
 *   so it reflects the real format even if the caller's mimeType label is wrong.
 * @param mimeType e.g. "image/png" — used as a fallback only if signature sniffing fails,
 *   and as the blob's Content-Type on upload.
 */
export async function uploadSketchImage(
  http: HttpClient,
  clientId: number,
  base64: string,
  mimeType: string,
  options: HttpRequestOptions = {},
): Promise<SketchToSlideUploadResult> {
  if (!base64) {
    throw new Error("No image data to upload — the captured sketch was empty.");
  }
  return uploadImageBlob({
    http,
    clientId,
    base64,
    mimeType,
    fileName: fileNameFor(base64, mimeType),
    options,
    messages: UPLOAD_MESSAGES,
    onError: logSketchToSlideError,
    reasons: UPLOAD_REASONS,
  });
}
